import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { debounce } from "lodash";
import { Box, Grid, Dialog, IconButton, Tooltip } from "@material-ui/core";

import { useTranslation } from "react-i18next";

import { getSellerShipments } from "api/shipment";

import { ExternalLink } from "components/Custom/Links";
import { StatusLabel } from "components/Custom";
import {
  Button,
  DatePickerGroup,
  Dropdown,
  InputContainer,
} from "components/Form";
import { FormattedTextField } from "components/FormElements";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";

import { useUserState } from "context/UserContext";

import {
  Class,
  Download2,
  ExportIcon,
  FolderSpecial,
  ImportIcon,
  Printer,
  QrCode2,
  TextSnippet,
} from "icons";

import { initialAPIListValue } from "utils/constants";
import { convertDataToTableViewObt } from "utils/convert";
import { convertDate, getPreviousDate, timezoneFormat } from "utils/date";
import { exportToExcel } from "utils/exportToExcel";
import getClearAdornment from "utils/form";
import parseJwt from "utils/jwt";
import { getWithExpiry } from "utils/storage";
import { toTitleCase } from "utils/string";

import ShipmentImport from "pages/shipments/ShipmentImport";
import { getCarrierTrackingLink } from "pages/shipments/utils";
import ShipmentPreview from "./ShipmentPreview";
import { downloadShipmentTemplate } from "./utils/shipment";
import * as XLSX from "xlsx";
import { popupNotification, useLayoutDispatch } from "context/LayoutContext";
import {
  ActionsButton,
  DeleteShipmentDialog,
  RequestCancellationDialog,
  RevertShipmentDialog,
  ShipmentCancelModal,
} from "pages/shipments/ShipmentActions";

export default function Shipments() {
  const layoutDispatch = useLayoutDispatch();

  const [t] = useTranslation();
  const claims = parseJwt(getWithExpiry("access"));
  const { seller_id } = claims;
  const { permissions } = useUserState();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [search, setSearch] = useState({
    is_small_carrier: true,
    seller_id: seller_id === 1 ? "" : seller_id,
    status: [],
    sku: "",
    po_number: "",
    ship_via: "",
    order__customer_id: "",
    start_date: convertDate(getPreviousDate(new Date(), 0, 0, 30)),
    end_date: convertDate(new Date()),
  });
  const [orderDirection, setOrderDirection] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [shipments, setShipments] = useState(initialAPIListValue);
  const [exportDisabled, setExportDisabled] = useState(false);

  const [headerCounts, setHeaderCounts] = useState({});
  const [shipment, setShipment] = useState(null);
  const [shipmentsImportData, setShipmentsImportData] = useState(
    initialAPIListValue,
  );
  const [cancelShipmentID, setCancelShipmentID] = useState(false);
  const [revertShipmentID, setRevertShipmentID] = useState("");
  const [cancellationOption, setCancellationOption] = useState("");
  const [deleteShipmentNumber, setDeleteShipmentNumber] = useState("");
  const [cancelRequestedShipmentID, setCancelRequestedShipmentID] = useState(
    false,
  );
  const history = useHistory();

  const cells = [
    {
      id: "customer",
      render: (r) => r.customer_id,
    },
    {
      id: "po_number",
      render: (r) => r.po_number,
      style: {
        minWidth: "150px",
      },
    },
    {
      id: "sku",
      render: (r, exported) =>
        exported ? (
          r?.sku
        ) : (
          <Tooltip
            title={
              r.detail_skus?.length > 1 ? (
                <div>
                  {r.detail_skus.slice(1).map((item, index) => (
                    <div key={index}>{item}</div>
                  ))}
                </div>
              ) : null
            }
            arrow={true}
            disableHoverListener={r.detail_skus?.length <= 1}
            disableFocusListener={r.detail_skus?.length <= 1}
            disableTouchListener={r.detail_skus?.length <= 1}
          >
            <span>
              {r.detail_skus?.[0] +
                (r.detail_skus?.length > 1
                  ? ` (+${r.detail_skus.length - 1})`
                  : "") || "-"}
            </span>
          </Tooltip>
        ),
      style: {
        minWidth: "150px",
      },
      sortable: true,
    },
    {
      id: "ship_via",
      render: (r) => toTitleCase(r.ship_via),
      style: {
        minWidth: "120px",
      },
    },
    {
      id: "tracking_number",
      render: (r, exported) =>
        exported ? (
          r.tracking_number
        ) : (
          <ExternalLink
            to={getCarrierTrackingLink(r.ship_via, r.tracking_number)}
            children={r.tracking_number}
          />
        ),
      style: {
        minWidth: "150px",
      },
      sortable: true,
    },

    {
      id: "status",
      render: (r, exported) =>
        exported ? (
          toTitleCase(r.status)
        ) : (
          <StatusLabel status={toTitleCase(r.status)} />
        ),
    },
    {
      id: "process_date",
      label: "process date",
      render: (r) => <span title={`Created at: ${timezoneFormat(r.created_date)}`}>{timezoneFormat(r.process_date)}</span>,
      style: {
        minWidth: "150px",
      },
      export: false,
      sortable: true,
    },
    {
      id: "actions",
      export: false,
      render: (r) => (
        <ActionsButton
          status={r.status}
          onCancelRequestClick={(option) => {
            setCancelRequestedShipmentID(r.id);
            setCancellationOption(option);
          }}
          onRevertClick={() => {
            setRevertShipmentID(r.id);
          }}
          onCancelClick={() => setCancelShipmentID(r.id)}
          onDeleteClick={() => setDeleteShipmentNumber(r.shipment_number)}
        />
      ),
    },
  ];

  const exportCells = [
    ...cells,
    {
      id: "process_date",
      label: "process date",
      render: (r) => timezoneFormat(r.process_date)
    },
    {
      id: "created_date",
      label: "created_at",
      render: (r) => timezoneFormat(r.created_date)
    },
  ]

  const handleFileChange = (e) => {
    const files = e.target.files;

    const f = files[0];
    const reader = new FileReader();

    reader.onload = function (e) {
      const data = e.target.result;
      const readedData = XLSX.read(data, {
        type: "binary",
        cellDates: true,
        dateNF: "yyyy-mm-dd",
      });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];

      const values = XLSX.utils
        .sheet_to_json(ws, { raw: false })
        .filter((v) => v["PO Number"] && v["Customer Name"] && v["SKU"]);

      if (!values.length) {
        popupNotification({
          dispatch: layoutDispatch,
          message:
            "Please add PO Number, Customer Name, SKU columns in your excel file",
          status: "error",
        });
        return;
      }

      if (values.length > 500) {
        popupNotification({
          dispatch: layoutDispatch,
          message: "Shipment count cannot be more than 500.",
          status: "error",
        });
        return;
      }

      // generate shipment and order #
      values.forEach((value, index) => {
        const first_char = value["Marketplace"][0].toLowerCase();
        const sn =
          "obt-" +
          first_char +
          value["Marketplace"]
            .slice(1)
            .toLowerCase()
            .replace(/[aeiou&%/?+#\s]/g, "")
            .substring(0, 2) +
          "-" +
          value["PO Number"];
        value["Shipment Number"] = sn;
        value["Order Number"] = sn;
        value["Line Number"] = index + 2;
      });

      setShipmentsImportData({
        count: values.length,
        items: values,
      });
    };
    reader.readAsBinaryString(f);
    e.target.value = "";
  };

  useEffect(() => {
    if (shipments.isFetching) {
      let params = {
        ...search,
        limit: rowsPerPage,
        offset: rowsPerPage * page,
      };
      if (orderDirection && orderBy) {
        params.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }
      getSellerShipments({
        params,
        setFunc: (res) => {
          setShipments(res);
          setHeaderCounts(res.customCounts);
        },
        paginated: true,
      });
    }
  }, [
    search,
    page,
    rowsPerPage,
    shipments.isFetching,
    orderDirection,
    orderBy,
  ]);

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setShipments({ ...shipments, isFetching: true });
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    setShipments((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeRowsPerPage = ({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    debouncedSearchHandler();
  };

  const handleChangeFilterSelect = (prop) => ({ target: { value } }) => {
    setSearch((prev) => ({ ...prev, [prop]: value }));
    debouncedSearchHandler();
  };

  const handleChangeFilterInput = (prop) => ({ target: { value } }) => {
    setSearch((prev) => ({ ...prev, [prop]: value }));
    debouncedSearchHandler();
  };

  const exportData = (data) => {
    const today = new Date();
    exportToExcel(
      [],
      `Shipments-${today.toLocaleDateString()}`,
      true,
      convertDataToTableViewObt(data, exportCells),
      setExportDisabled,
    );
  };

  const handleExportData = () => {
    if (shipments.items.length === shipments.count) {
      exportData(shipments.items);
    } else {
      getSellerShipments({
        params: {
          ...search,
          limit: shipments.count,
        },
        setFunc: ({ items: { results } }) => exportData(results),
      });
    }
  };

  const clearHandler = (input) => {
    setSearch({ ...search, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, search);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce(() => {
      setPage(0);
      setShipments({ ...shipments, isFetching: true });
    }, 500),
    [],
  );

  return (
    <>
      <PageTitle
        window_title="Ecommerce Shipments"
        title={
          <>
            Ecommerce Shipments
            {
              <IconButton onClick={downloadShipmentTemplate}>
                <Download2 color="#FFF" />
              </IconButton>
            }
          </>
        }
        multipleButtons={true}
        button={
          <>
            <Button
              endIcon={<ImportIcon color="white" />}
              variant="contained"
              color="primary-light"
              component="label"
            >
              Import
              <input
                id="files"
                hidden
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                type="file"
                onChange={handleFileChange}
              />
            </Button>
            {shipments?.items?.length !== 0 && (
              <Button
                endIcon={<ExportIcon color="white" />}
                variant="contained"
                size="medium"
                color="primary-light"
                onClick={() => handleExportData()}
              >
                Export
              </Button>
            )}
          </>
        }
      />

      {shipmentsImportData?.count ? (
        <ShipmentImport
          open={true}
          shipments={shipmentsImportData}
          close={(status) => {
            setShipmentsImportData(initialAPIListValue);
            if (status) {
              setShipments((prev) => ({ ...prev, isFetching: true }));
            }
          }}
        />
      ) : null}

      <InputContainer>
        <Grid container>
          <DatePickerGroup
            startDateValue={search.start_date}
            startDateOnChange={handleChangeFilterSelect("start_date")}
            endDateValue={search.end_date}
            endDateOnChange={handleChangeFilterSelect("end_date")}
            itemGridProps={{ xs: 12, sm: 6, md: 4, lg: 2, xl: 2 }}
          />
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="customer"
              label={t("customer")}
              variant="outlined"
              fullWidth
              value={search.order__customer_id}
              onChange={handleChangeFilterInput("order__customer_id")}
              InputProps={clearAdornment("order__customer_id")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="sku"
              label={t("SKU")}
              variant="outlined"
              fullWidth
              value={search.sku}
              onChange={handleChangeFilterInput("sku")}
              InputProps={clearAdornment("sku")}
              LeftIcon={QrCode2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="po_number"
              label="PO number"
              variant="outlined"
              fullWidth
              value={search.po_number}
              onChange={handleChangeFilterInput("po_number")}
              InputProps={clearAdornment("po_number")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="ship_via"
              label={t("ship_via")}
              variant="outlined"
              fullWidth
              value={search.ship_via}
              onChange={handleChangeFilterInput("ship_via")}
              InputProps={clearAdornment("ship_via")}
              LeftIcon={TextSnippet}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Dropdown
              id="status"
              title={t("status")}
              value={search.status}
              onChange={handleChangeFilterSelect("status")}
              items={[
                "Open",
                "Confirmed",
                "Printed",
                "Picked",
                "Shipped",
                "Partial_Shipped",
                "Invoiced",
                "Errored",
                "Pending_Cancellation",
                "Cancelled",
                "Out_of_Stock",
              ].map((item) => ({
                value: item,
                label: item,
              }))}
              all_option_active={false}
              LeftIcon={Class}
              multiple
              checkmark={true}
            />
          </Grid>
          <Box
            component={Grid}
            item
            lg={
              permissions?.includes("contenttypes.shipper.download_labels")
                ? 0
                : 3
            }
            display={{
              xs: "none",
              sm: "none",
              md: "none",
              lg: "block",
            }}
            style={{ borderRight: "none" }}
          />
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Button
            variant="contained"
            size="medium"
            color="secondary"
            onClick={() => {
              history.push("/app/shipments-to-print");
            }}
          >
            <Printer color="white" />{" "}
            <span style={{ marginLeft: 5 }}>Shipments To Print</span>
          </Button>
        </Grid>
        </Grid>
      </InputContainer>
      <Grid container spacing={4}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <DefaultTable
            data={shipments}
            headers={cells}
            headerCounts={headerCounts}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            disableExportData={true}
            handleExportData={() => handleExportData()}
            sortActions={{ onSort, orderBy, orderDirection }}
            exportDisabled={exportDisabled}
            setExportDisabled={setExportDisabled}
            filters={search}
          />
          {shipment ? (
            <Dialog
              open={true}
              onClose={() => setShipment(null)}
              maxWidth="md"
              fullWidth
            >
              <ShipmentPreview shipment={shipment} />{" "}
            </Dialog>
          ) : null}
        </Grid>
      </Grid>
      <ShipmentCancelModal
        shipment_id={cancelShipmentID}
        close={() => setCancelShipmentID(false)}
        afterCancelled={() => setShipments({ ...shipments, isFetching: true })}
      />
      <RequestCancellationDialog
        shipment_id={cancelRequestedShipmentID}
        close={() => setCancelRequestedShipmentID(false)}
        afterCancelled={() => setShipments({ ...shipments, isFetching: true })}
        option={cancellationOption}
      />
      <DeleteShipmentDialog
        shipment_number={deleteShipmentNumber}
        close={(status = false) => {
          if (status) {
            setShipments({ ...shipments, isFetching: true });
          }
          setDeleteShipmentNumber("");
        }}
      />
      <RevertShipmentDialog
        shipment_id={revertShipmentID}
        close={(status = false) => {
          if (status) {
            setShipments({ ...shipments, isFetching: true });
          }
          setRevertShipmentID("");
        }}
      />
    </>
  );
}
